import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import SProductFiles from "./SProductFiles";
import SProductInformation from "./SProductInformation";
import ImgWithSignedUrl from "components/SignedUrl/ImgWithSignedUrl";

export default function SProductsAddUpdate({
  formData,
  setFormData,
  isUpdateMode,
  setShowAddModal,
  setProductsData,
  setIsUpdateMode,
  refreshFunc,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.userInfo);

  // Safely parse a value that may be a JSON string, array, or null
  const safeArray = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const normalizeProductData = (product) => {
    if (!product) return product;
    return {
      ...product,
      colors: safeArray(product.colors),
      variations: safeArray(product.variations),
      sizes: safeArray(product.sizes),
    };
  };

  const refreshProductFromList = async () => {
    if (!refreshFunc) return null;
    const list = await refreshFunc();
    if (Array.isArray(list) && formData?.id) {
      const match = list.find((item) => item.id === formData.id);
      if (match) {
        const normalized = normalizeProductData(match);
        setFormData((prev) => ({ ...prev, ...normalized }));
        return normalized;
      }
    }
    return null;
  };

  /*   All States
   ********************************************* */
  const [loading, setLoading] = useState(false);
  const [sizes, setSizes] = useState([]);
  const [sizesLoading, setSizesLoading] = useState(false);
  const [fabrics, setFabrics] = useState([]);
  const [fabricsLoading, setFabricsLoading] = useState(false);

  const [activeTab, setActiveTab] = useState("product");

  const [productTypes, setProductTypes] = useState([]);

  // Colors
  const [colorInput, setColorInput] = useState("");
  const [teamInput, setTeamInput] = useState("");
  const [editingColorIndex, setEditingColorIndex] = useState(null);

  // Variations
  const [variationsLoading, setVariationsLoading] = useState(false);
  const [variations, setVariations] = useState([]);

  // Sizes Modal
  const [showSizesModal, setShowSizesModal] = useState(false);
  const [pendingSizes, setPendingSizes] = useState([]);
  const [sizeSearch, setSizeSearch] = useState("");
  const [sizeDisplayCount, setSizeDisplayCount] = useState(20);

  // Variation Image Modal
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [variationImageFile, setVariationImageFile] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  /*   All Functions
   ********************************************* */

  // Size sets (Products -> Sizes -> Size Sets) for the Product Info card's
  // Size Sets picker (user, 2026-10-30). Adding this product to a WO offers
  // the picked sets for quantities.
  const [sizeSets, setSizeSets] = useState([]);
  useEffect(() => {
    API.post("/sales/size-sets", {}, { headers: authHeader() })
      .then(({ data }) => setSizeSets(data?.data || []))
      .catch(() => {});
  }, []);
  const pickedSetIds = safeArray(formData?.sizeSets).map(String);
  const setPickedSets = (ids) =>
    setFormData((prev) => ({ ...prev, sizeSets: ids.map(Number) }));
  const [sizesTab, setSizesTab] = useState("sets");
  // Open on Custom Sizes for a product that has only custom sizes.
  const hasSets = pickedSetIds.length > 0;
  const hasCustom = safeArray(formData?.sizes).length > 0;
  useEffect(() => {
    setSizesTab(!hasSets && hasCustom ? "custom" : "sets");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.id]);

  // Fetch Sizes
  const fetchSizes = async () => {
    try {
      setSizesLoading(true);
      const { data } = await API.post(
        "sales/products/sizes",
        { limit: 100, page: 1 },
        { headers: authHeader() },
      );
      if (data?.success) {
        setSizes(data.data);
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setSizesLoading(false);
    }
  };

  // Fetch Fabrics
  const fetchFabrics = async () => {
    try {
      setFabricsLoading(true);
      const { data } = await API.post(
        "sales/products/fabrics",
        { limit: 100, page: 1, filterBy: "all" },
        { headers: authHeader() },
      );
      if (data?.success) {
        setFabrics(data.data);
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setFabricsLoading(false);
    }
  };

  // Fetch product types
  const fetchProductTypes = async () => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "sales/products/product-types",
        { page: 1, limit: 100, filterBy: "all" },
        { headers: authHeader() },
      );
      if (data.success) {
        setProductTypes(data.data);
      } else {
        dispatch(
          setNotification({
            message: data.message,
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle Color Add / Update
  const handleAddColor = () => {
    const color = colorInput.trim();
    const team = teamInput.trim();
    if (!color) return;
    const combined = team ? `${color}-${team}` : color;
    const existing = formData.colors || [];
    if (editingColorIndex !== null) {
      const oldCombined = existing[editingColorIndex];
      const updatedColors = [...existing];
      updatedColors[editingColorIndex] = combined;
      // Keep variations in sync: rename color & variationName, preserve images and all other fields
      const updatedVariations = safeArray(formData.variations).map((v) => {
        if (v.color === oldCombined) {
          return {
            ...v,
            color: combined,
            variationName: `${v.styleId || formData.styleId}-${combined}`,
          };
        }
        return v;
      });
      setFormData({
        ...formData,
        colors: updatedColors,
        variations: updatedVariations,
      });
      setEditingColorIndex(null);
      setColorInput("");
      setTeamInput("");
      return;
    }
    if (existing.map((c) => c.toLowerCase()).includes(combined.toLowerCase()))
      return;
    setFormData({ ...formData, colors: [...existing, combined] });
    setColorInput("");
    setTeamInput("");
  };

  // Handle Color Edit (start)
  const handleEditColor = (index) => {
    const val = (formData.colors || [])[index] || "";
    const dashIdx = val.indexOf("-");
    if (dashIdx !== -1) {
      setColorInput(val.slice(0, dashIdx));
      setTeamInput(val.slice(dashIdx + 1));
    } else {
      setColorInput(val);
      setTeamInput("");
    }
    setEditingColorIndex(index);
  };

  // Handle Color Remove
  const handleRemoveColor = (index) => {
    const updated = [...(formData.colors || [])];
    updated.splice(index, 1);
    if (editingColorIndex === index) {
      setEditingColorIndex(null);
      setColorInput("");
      setTeamInput("");
    }
    setFormData({ ...formData, colors: updated });
  };

  // Create Or Update Variations
  const createOrUpdateVariations = async () => {
    const colors = formData.colors || [];
    if (!formData.styleId) {
      dispatch(
        setNotification({
          message: "Style ID is required to create variations.",
          type: "error",
        }),
      );
      return;
    }
    if (colors.length === 0) {
      dispatch(
        setNotification({
          message: "Please add at least one color.",
          type: "error",
        }),
      );
      return;
    }
    const existingVariations = safeArray(formData.variations);
    const generated = colors.map((color) => {
      const existing = existingVariations.find((v) => v.color === color);
      return {
        ...(existing || {}),
        productId: formData.id,
        styleId: formData.styleId,
        color,
        variationName: `${formData.styleId}-${color}`,
      };
    });
    try {
      setVariationsLoading(true);
      const { data } = await API.patch(
        "/sales/product",
        { ...formData, colors, variations: generated },
        { headers: authHeader() },
      );
      if (data?.success) {
        const nextVariations = data.data?.variations || generated;
        setVariations(nextVariations);
        setFormData((prev) => ({
          ...prev,
          colors,
          variations: nextVariations,
        }));
        await refreshProductFromList();
        dispatch(
          setNotification({
            message: data.message || "Variations created/updated.",
            type: "success",
          }),
        );
      } else {
        dispatch(
          setNotification({
            message: data.message || "Failed to save variations.",
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setVariationsLoading(false);
    }
  };

  // Upload image to a variation
  const handleUploadVariationImage = async () => {
    if (!variationImageFile || !selectedVariation) return;

    const folder = `kms/sales/product/${user?.companyId}/${formData?.id}/variations`;
    const imageKey = `${folder}/${variationImageFile.name}`;

    // Build updated variations with the new image key added to the target variation
    const allVariations = safeArray(formData.variations);
    const updatedVariations = allVariations.map((v) => {
      if (
        v.variationName === selectedVariation.variationName &&
        v.color === selectedVariation.color
      ) {
        return { ...v, images: [imageKey] };
      }
      return v;
    });

    const payload = new FormData();
    payload.append("file", variationImageFile);
    payload.append("folder", folder);
    payload.append("id", formData?.id);
    payload.append("variations", JSON.stringify(updatedVariations));

    try {
      setImageUploading(true);
      const { data } = await API.patch("/sales/product/variations", payload, {
        headers: { "Content-Type": "multipart/form-data", ...authHeader() },
      });
      if (data === "success" || data?.success) {
        // Sync formData and local variation states
        setFormData((prev) => ({ ...prev, variations: updatedVariations }));
        const updatedSelected = updatedVariations.find(
          (v) =>
            v.variationName === selectedVariation.variationName &&
            v.color === selectedVariation.color,
        );
        setSelectedVariation(updatedSelected || selectedVariation);
        if (variations.length > 0) setVariations(updatedVariations);
        const refreshed = await refreshProductFromList();
        if (refreshed?.variations?.length) {
          const latestSelected = refreshed.variations.find(
            (v) =>
              v.variationName === selectedVariation.variationName &&
              v.color === selectedVariation.color,
          );
          if (latestSelected) {
            setSelectedVariation(latestSelected);
            setVariations(refreshed.variations);
          }
        }
        dispatch(
          setNotification({ message: "Image uploaded.", type: "success" }),
        );
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Upload failed.",
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setImageUploading(false);
      setVariationImageFile(null);
    }
  };

  // Delete image from a variation
  const handleDeleteVariationImage = async (imageKey) => {
    if (!selectedVariation) return;

    const allVariations = safeArray(formData.variations);
    const updatedVariations = allVariations.map((v) => {
      if (
        v.variationName === selectedVariation.variationName &&
        v.color === selectedVariation.color
      ) {
        return {
          ...v,
          images: (v.images || []).filter((img) => img !== imageKey),
        };
      }
      return v;
    });

    try {
      const { data } = await API.post(
        "/sales/product/variations/delete",
        { file: imageKey, id: formData?.id, variations: updatedVariations },
        { headers: authHeader() },
      );
      if (data?.success) {
        setFormData((prev) => ({ ...prev, variations: updatedVariations }));
        const updatedSelected = updatedVariations.find(
          (v) =>
            v.variationName === selectedVariation.variationName &&
            v.color === selectedVariation.color,
        );
        setSelectedVariation(updatedSelected || selectedVariation);
        if (variations.length > 0) setVariations(updatedVariations);
        const refreshed = await refreshProductFromList();
        if (refreshed?.variations?.length) {
          const latestSelected = refreshed.variations.find(
            (v) =>
              v.variationName === selectedVariation.variationName &&
              v.color === selectedVariation.color,
          );
          if (latestSelected) {
            setSelectedVariation(latestSelected);
            setVariations(refreshed.variations);
          }
        }
        dispatch(
          setNotification({ message: "Image deleted.", type: "success" }),
        );
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Delete failed.",
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    }
  };

  const updateVariationsOnProduct = async (nextVariations) => {
    const { data } = await API.patch(
      "/sales/product",
      { ...formData, variations: nextVariations },
      { headers: authHeader() },
    );

    if (!data?.success) {
      throw new Error(data?.message || "Failed to update variations.");
    }

    setFormData((prev) => ({ ...prev, variations: nextVariations }));
    setVariations(nextVariations);
  };

  const deleteVariationImagesFromStorage = async (
    imageKeys,
    nextVariations,
  ) => {
    if (!Array.isArray(imageKeys) || imageKeys.length === 0) {
      return;
    }

    const results = await Promise.allSettled(
      imageKeys.map((file) =>
        API.post(
          "/sales/product/variations/delete",
          { file, id: formData?.id, variations: nextVariations },
          { headers: authHeader() },
        ),
      ),
    );

    const failedCount = results.filter(
      (result) =>
        !(result.status === "fulfilled" && result.value?.data?.success),
    ).length;

    if (failedCount > 0) {
      throw new Error(
        `${failedCount} image${failedCount > 1 ? "s" : ""} could not be deleted.`,
      );
    }
  };

  const handleDeleteVariation = async (variation) => {
    if (!variation) return;

    const confirmed = window.confirm(
      `Delete variation ${variation.variationName}? This will also delete all attached images.`,
    );
    if (!confirmed) return;

    try {
      setVariationsLoading(true);

      const allVariations = safeArray(formData.variations);
      const nextVariations = allVariations.filter(
        (v) =>
          !(
            v.variationName === variation.variationName &&
            v.color === variation.color
          ),
      );

      const target = allVariations.find(
        (v) =>
          v.variationName === variation.variationName &&
          v.color === variation.color,
      );

      const imageKeys = safeArray(target?.images);
      await deleteVariationImagesFromStorage(imageKeys, nextVariations);
      await updateVariationsOnProduct(nextVariations);

      if (
        selectedVariation?.variationName === variation.variationName &&
        selectedVariation?.color === variation.color
      ) {
        setShowImageModal(false);
        setSelectedVariation(null);
        setVariationImageFile(null);
      }

      await refreshProductFromList();

      dispatch(
        setNotification({
          message: "Variation deleted successfully.",
          type: "success",
        }),
      );
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setVariationsLoading(false);
    }
  };

  const handleDeleteAllVariations = async () => {
    const allVariations = safeArray(formData.variations);
    if (allVariations.length === 0) {
      return;
    }

    const confirmed = window.confirm(
      "Delete all variations? This will also delete all variation images.",
    );
    if (!confirmed) return;

    try {
      setVariationsLoading(true);

      const allImageKeys = [
        ...new Set(allVariations.flatMap((v) => safeArray(v.images))),
      ];

      await deleteVariationImagesFromStorage(allImageKeys, []);
      await updateVariationsOnProduct([]);

      setShowImageModal(false);
      setSelectedVariation(null);
      setVariationImageFile(null);

      await refreshProductFromList();

      dispatch(
        setNotification({
          message: "All variations deleted successfully.",
          type: "success",
        }),
      );
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setVariationsLoading(false);
    }
  };

  // Handle Size Selection
  const handleSizeChange = (sizeId) => {
    const selectedSizes = formData.sizes || [];
    const sizeIndex = selectedSizes.indexOf(sizeId);

    if (sizeIndex === -1) {
      // Add size if not already selected
      setFormData({
        ...formData,
        sizes: [...selectedSizes, sizeId],
      });
    } else {
      // Remove size if already selected
      setFormData({
        ...formData,
        sizes: selectedSizes.filter((id) => id !== sizeId),
      });
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Adds Button Function
  const AddProductsData = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { data } = await API.post("/sales/product", formData, {
        headers: authHeader(),
      });
      if (data?.success) {
        // setProductsData((prev) => [...prev, data?.data]);
        await refreshFunc();
        setFormData({});
        setShowAddModal(false);
        dispatch(
          setNotification({
            message: data?.message,
            type: "success",
          }),
        );
      } else {
        dispatch(
          setNotification({
            message: data?.message,
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }

    setShowAddModal(false);
  };

  // Update Button Function
  const UpdateProductsData = async (e, closeModal) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { data } = await API.patch("/sales/product", formData, {
        headers: authHeader(),
      });
      if (data?.success) {
        await refreshFunc();
        if (closeModal) {
          setIsUpdateMode(false);
          setShowAddModal(false);
        }
        dispatch(
          setNotification({
            message: data?.message,
            type: "success",
          }),
        );
      } else {
        dispatch(
          setNotification({
            message: data?.message,
            type: "error",
          }),
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  /*   All UseEffects
   ********************************************* */ useEffect(() => {
    fetchSizes();
    fetchFabrics();
    fetchProductTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Normalize JSON string fields from API into arrays whenever formData changes
  useEffect(() => {
    const colors = safeArray(formData.colors);
    const variations = safeArray(formData.variations);
    const sizes = safeArray(formData.sizes);
    // Only patch if any field is not already an array
    if (
      !Array.isArray(formData.colors) ||
      !Array.isArray(formData.variations) ||
      !Array.isArray(formData.sizes)
    ) {
      setFormData((prev) => ({
        ...prev,
        colors,
        variations,
        sizes,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.id]);

  const displayVariations =
    variations.length > 0 ? variations : safeArray(formData.variations);

  return (
    <div>
      {loading && <Spinner />}

      <div className="">
        <h4>{isUpdateMode ? "Update Product" : "Add Product"}</h4>
      </div>
      <hr className="mt-0 mb-2" />
      <ul className="mb-3 nav nav-tabs nav-bordered border-0 d-flex flex-grow-1">
        <li className="">
          <button
            className={
              activeTab === "product"
                ? `btn btn-primary active bg-primary text-white`
                : "btn bg-white shadow-sm"
            }
            onClick={() => setActiveTab("product")}
          >
            <i className="mdi mdi-settings-outline d-md-none d-block"></i>
            <span className="d-none d-md-block">Product</span>
          </button>
        </li>

        <li className="">
          <button
            className={
              activeTab === "variations"
                ? `btn btn-primary active bg-primary text-white`
                : "btn bg-white shadow-sm"
            }
            onClick={() => setActiveTab("variations")}
          >
            <i className="mdi mdi-settings-outline d-md-none d-block"></i>
            <span className="d-none d-md-block">Image Variations</span>
          </button>
        </li>
        {/* Two tabs again since 2026-10-16: Product Information now holds the
            Default card and the location overrides side by side, so
            Attachments went back to a tab of its own. */}
        <li className="">
          <button
            className={
              activeTab === "information"
                ? `btn btn-primary active bg-primary text-white`
                : "btn bg-white shadow-sm"
            }
            onClick={() => setActiveTab("information")}
          >
            <i className="mdi mdi-information-outline d-md-none d-block"></i>
            <span className="d-none d-md-block">Product Information</span>
          </button>
        </li>
        <li className="">
          <button
            className={
              activeTab === "files"
                ? `btn btn-primary active bg-primary text-white`
                : "btn bg-white shadow-sm"
            }
            onClick={() => setActiveTab("files")}
          >
            <i className="mdi mdi-paperclip d-md-none d-block"></i>
            <span className="d-none d-md-block">Attachments</span>
          </button>
        </li>
      </ul>

      {activeTab === "product" && (
        <form
          onSubmit={isUpdateMode ? UpdateProductsData : AddProductsData}
          className="row align-items-stretch"
        >
          <div className="col-md-4 d-flex flex-column">
            <div className="card border-0 shadow-sm h-100 d-flex flex-column">
              <div className="card-header bg-secondary bg-opacity-25 border-0 py-1">
                <h5 className="text-secondary ">
                  <i className="mdi mdi-information-outline mr-1"></i> Product
                  Info
                </h5>
              </div>
              <div className="card-body p-2 flex-grow-1">
                <div className="mb-2">
                  <label htmlFor="productTypeId" className="form-label">
                    Product Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select w-100"
                    id="productTypeId"
                    name="productTypeId"
                    value={formData?.productTypeId || ""}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Product Type</option>
                    {productTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.productTypes}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-2">
                  <label className="form-label font-weight-bold">
                    Style ID
                  </label>
                  <div className="">
                    <input
                      type="text"
                      value={formData?.styleId || ""}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          styleId: e.target.value,
                        });
                      }}
                      className="form-control"
                      placeholder="Enter Style ID"
                    />
                  </div>
                </div>
                <div className="mb-2">
                  <label className="form-label font-weight-bold">
                    Description
                  </label>
                  <div className="">
                    <input
                      type="text"
                      value={formData?.desc || ""}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          desc: e.target.value,
                        });
                      }}
                      className="form-control"
                      placeholder="Enter Description"
                    />
                  </div>
                </div>
                <div className="mb-2">
                  <label htmlFor="fabricId" className="form-label">
                    Fabric
                  </label>
                  <select
                    className="form-select w-100"
                    id="fabricId"
                    name="fabricId"
                    value={formData?.fabricId || ""}
                    onChange={handleChange}
                  >
                    <option value="">Select Fabric</option>
                    {fabrics.map((fabric) => (
                      <option key={fabric.fabricId} value={fabric.fabricId}>
                        {fabric.name}
                      </option>
                    ))}
                  </select>
                  {fabricsLoading && (
                    <small className="text-muted">
                      <i className="mdi mdi-loading mdi-spin"></i> Loading
                      fabrics...
                    </small>
                  )}
                </div>

                {/* Product Url moved to the Product Information tab, beside
                    the two PDFs it belongs with. Keeping a copy here as well
                    would give one value two editors. */}
                {/* Brand Name */}
                <div className="mb-2">
                  <label className="form-label font-weight-bold">
                    Brand Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData?.brandName || ""}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        brandName: e.target.value,
                      });
                    }}
                    placeholder="Enter Brand Name"
                  />
                </div>

              </div>
            </div>
          </div>

          {/* Colors */}
          <div className="col-md-5 h-d-flex flex-column">
            {/* <label className="form-label font-weight-bold mb-2">Colors</label> */}
            <div className="card border-0 shadow-sm flex-grow-1 d-flex flex-column">
              <div className="card-header bg-info bg-opacity-25 border-0 py-1">
                <h5 className="text-info ">
                  <i className="mdi mdi-palette mr-1"></i> Colors
                </h5>
              </div>
              <div className="card-body p-2 flex-grow-1 d-flex flex-column">
                <div className="row g-1 mb-2">
                  <div className="col">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Color (e.g. Red)"
                      value={colorInput}
                      onChange={(e) => setColorInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddColor();
                        }
                      }}
                    />
                  </div>
                  <div className="col">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Team Name"
                      value={teamInput}
                      onChange={(e) => setTeamInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddColor();
                        }
                      }}
                    />
                  </div>
                  <div className="col-auto d-flex gap-1">
                    {editingColorIndex !== null && (
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          setEditingColorIndex(null);
                          setColorInput("");
                          setTeamInput("");
                        }}
                        title="Cancel edit"
                      >
                        <i className="mdi mdi-close"></i>
                      </button>
                    )}
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        editingColorIndex !== null ? "btn-warning" : "btn-info"
                      }`}
                      onClick={handleAddColor}
                    >
                      {editingColorIndex !== null ? (
                        <>
                          <i className="mdi mdi-check"></i> Update
                        </>
                      ) : (
                        <>
                          <i className="mdi mdi-plus"></i> Add
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <div
                  className="flex-grow-1"
                  style={{ height: 400, overflowY: "auto" }}
                >
                  {(formData.colors || []).length === 0 ? (
                    <div
                      className="text-center text-muted py-3"
                      style={{ fontSize: "13px" }}
                    >
                      <i
                        className="mdi mdi-information-outline d-block mb-1"
                        style={{ fontSize: "20px" }}
                      ></i>
                      No Colors added
                    </div>
                  ) : (
                    (formData.colors || []).map((color, idx) => (
                      <div
                        key={idx}
                        className="d-flex align-items-center justify-content-between px-2 py-1 mb-1 rounded"
                        style={{ background: "#e8f4f8" }}
                      >
                        <span style={{ fontSize: "13px" }}>
                          <i className="mdi mdi-circle-small text-info"></i>{" "}
                          {color}
                        </span>
                        <div className="d-flex gap-1">
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-warning"
                            onClick={() => handleEditColor(idx)}
                            title="Edit color"
                          >
                            <i className="mdi mdi-pencil"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-danger"
                            onClick={() => handleRemoveColor(idx)}
                            title="Remove color"
                          >
                            <i className="mdi mdi-close"></i>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="card-footer bg-white border-top border-1 py-1">
                <small className="text-muted">
                  Count: {(formData.colors || []).length}
                </small>
              </div>
            </div>
          </div>

          {/* Sizes Selection */}
          <div className="col-md-3 d-flex flex-column">
            <div className="card border-0 shadow-sm d-flex flex-column h-100">
              <div className="card-header bg-primary bg-opacity-25 border-0 py-1 d-flex align-items-center justify-content-between">
                <h5 className="text-primary">
                  <i className="mdi mdi-ruler mr-1"></i> Sizes
                </h5>
              </div>
              {/* Two tabs (user, 2026-11-02): Size Sets | Custom Sizes. With a
                  set picked, Custom Sizes can still be opened but is off. */}
              <ul className="nav nav-tabs nav-fill px-2 pt-1 small">
                {[
                  ["sets", "Size Sets"],
                  ["custom", "Custom Sizes"],
                ].map(([key, label]) => {
                  const off = key === "custom" && pickedSetIds.length > 0;
                  return (
                    <li key={key} className="nav-item">
                      <button
                        type="button"
                        className={`nav-link py-1 ${sizesTab === key ? "active" : ""} ${off ? "text-muted" : ""}`}
                        title={off ? "Off while a size set is selected" : undefined}
                        onClick={() => setSizesTab(key)}
                      >
                        {off && <i className="mdi mdi-lock-outline me-1"></i>}
                        {label}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div
                className="card-body p-2 flex-grow-1 pt-1"
                style={{ height: 380, overflowY: "auto" }}
              >
                {sizesTab === "sets" ? (
                  <div className="pt-1">
                      <select
                        className="form-select w-100"
                        aria-label="Add size set"
                        value=""
                        onChange={(e) => {
                          if (e.target.value)
                            setPickedSets([...pickedSetIds, e.target.value]);
                        }}
                      >
                        <option value="">Add size set...</option>
                        {sizeSets
                          .filter((set) => !pickedSetIds.includes(String(set.id)))
                          .map((set) => (
                            <option key={set.id} value={set.id}>
                              {set.name}
                            </option>
                          ))}
                      </select>
                      {/* One row per set (user, 2026-11-02), like Custom Sizes. */}
                      <div className="mt-2">
                        {pickedSetIds.map((id) => {
                          const set = sizeSets.find((x) => String(x.id) === id);
                          const names = (set?.items || [])
                            .map((i) => i.size?.name)
                            .filter(Boolean)
                            .join(", ");
                          return (
                            <div
                              key={id}
                              className="d-flex align-items-center justify-content-between px-2 py-1 mb-1 rounded"
                              style={{ background: "#eaf0fb" }}
                            >
                              <div className="min-w-0" style={{ fontSize: "13px" }}>
                                <div>
                                  <i className="mdi mdi-circle-small text-primary"></i>{" "}
                                  {set?.name || `Set ${id}`}
                                </div>
                                {names && (
                                  <div className="text-muted text-truncate ps-3" style={{ fontSize: 11 }} title={names}>
                                    {names}
                                  </div>
                                )}
                              </div>
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-danger"
                                title="Remove size set"
                                aria-label={`Remove ${set?.name || "size set"}`}
                                onClick={() =>
                                  setPickedSets(pickedSetIds.filter((x) => x !== id))
                                }
                              >
                                <i className="mdi mdi-close"></i>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    {!pickedSetIds.length && (
                      <div className="text-muted small mt-2">
                        No size set selected. Pick one, or use Custom Sizes.
                      </div>
                    )}
                  </div>
                ) : (
                <fieldset
                  disabled={pickedSetIds.length > 0}
                  style={pickedSetIds.length > 0 ? { opacity: 0.5 } : undefined}
                >
                {pickedSetIds.length > 0 && (
                  <div className="alert alert-secondary py-1 px-2 small mb-2">
                    Off while a size set is selected.
                  </div>
                )}
                <div className="d-flex mb-2 align-items-center justify-content-between border-bottom border-1">
                  <h5
                    className=""
                    style={{
                      fontSize: 16,
                    }}
                  >
                    Select Sizes
                  </h5>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm "
                    onClick={() => {
                      setPendingSizes([...(formData.sizes || [])]);
                      setSizeSearch("");
                      setSizeDisplayCount(20);
                      setShowSizesModal(true);
                    }}
                    title="Edit Sizes"
                  >
                    <i className="mdi mdi-pencil"></i> Edit
                  </button>
                </div>

                {(formData.sizes || []).length === 0 ? (
                  <div
                    className="text-center text-muted py-3"
                    style={{ fontSize: "13px" }}
                  >
                    <i
                      className="mdi mdi-information-outline d-block mb-1"
                      style={{ fontSize: "20px" }}
                    ></i>
                    No sizes selected
                  </div>
                ) : (
                  <div>
                    {(formData.sizes || []).map((sizeId) => {
                      const sizeObj = sizes.find((s) => s.id === sizeId);
                      return (
                        <div
                          key={sizeId}
                          className="d-flex align-items-center justify-content-between px-2 py-1 mb-1 rounded"
                          style={{ background: "#eaf0fb" }}
                        >
                          <span style={{ fontSize: "13px" }}>
                            <i className="mdi mdi-circle-small text-primary"></i>{" "}
                            {sizeObj ? sizeObj.name : sizeId}
                          </span>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-danger"
                            onClick={() => handleSizeChange(sizeId)}
                            title="Remove size"
                          >
                            <i className="mdi mdi-close"></i>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                </fieldset>
                )}
              </div>
              <div className="card-footer bg-white border-top border-1 py-1">
                <small className="text-muted">
                  {pickedSetIds.length > 0
                    ? `Size sets: ${pickedSetIds.length}`
                    : `Selected: ${(formData.sizes || []).length} sizes`}
                </small>
              </div>
            </div>
          </div>

          {/* Sizes Modal */}
          <Modal
            show={showSizesModal}
            onHide={() => setShowSizesModal(false)}
            scrollable
            contentClassName="shadow-lg"
          >
            <Modal.Header closeButton className="py-2">
              <Modal.Title style={{ fontSize: "16px" }}>
                <i className="mdi mdi-ruler mr-1"></i> Select Sizes
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-2 pb-1">
              {/* Search */}
              <div className="mb-2">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white border-end-0">
                    <i className="mdi mdi-magnify text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    placeholder="Search sizes..."
                    value={sizeSearch}
                    onChange={(e) => {
                      setSizeSearch(e.target.value);
                      setSizeDisplayCount(20);
                    }}
                  />
                  {sizeSearch && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary border-start-0"
                      onClick={() => {
                        setSizeSearch("");
                        setSizeDisplayCount(20);
                      }}
                    >
                      <i className="mdi mdi-close"></i>
                    </button>
                  )}
                </div>
              </div>

              {sizesLoading ? (
                <div className="text-center py-3 text-muted">
                  <i className="mdi mdi-loading mdi-spin me-1"></i> Loading
                  sizes...
                </div>
              ) : (
                (() => {
                  const filtered = sizes.filter((s) =>
                    s.name.toLowerCase().includes(sizeSearch.toLowerCase()),
                  );
                  const visible = filtered.slice(0, sizeDisplayCount);
                  if (filtered.length === 0) {
                    return (
                      <div
                        className="text-center text-muted py-3"
                        style={{ fontSize: "13px" }}
                      >
                        <i
                          className="mdi mdi-magnify-close d-block mb-1"
                          style={{ fontSize: "20px" }}
                        ></i>
                        No sizes match &ldquo;{sizeSearch}&rdquo;
                      </div>
                    );
                  }
                  return (
                    <div>
                      {visible.map((size) => {
                        const isSelected = pendingSizes.includes(size.id);
                        return (
                          <div
                            key={size.id}
                            className={`d-flex align-items-center px-2 py-2 mb-1 rounded ${
                              isSelected
                                ? "bg-primary bg-opacity-10"
                                : "bg-light"
                            }`}
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              setPendingSizes((prev) =>
                                prev.includes(size.id)
                                  ? prev.filter((id) => id !== size.id)
                                  : [...prev, size.id],
                              )
                            }
                          >
                            <input
                              type="checkbox"
                              className="me-2"
                              readOnly
                              checked={isSelected}
                              style={{ pointerEvents: "none" }}
                            />
                            <span style={{ fontSize: "14px" }}>
                              {size.name}
                            </span>
                          </div>
                        );
                      })}
                      {sizeDisplayCount < filtered.length && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm w-100 mt-1"
                          onClick={() => setSizeDisplayCount((c) => c + 20)}
                        >
                          <i className="mdi mdi-chevron-down me-1"></i>
                          Load more ({filtered.length - sizeDisplayCount}{" "}
                          remaining)
                        </button>
                      )}
                    </div>
                  );
                })()
              )}
            </Modal.Body>
            <Modal.Footer className="py-2">
              <small className="text-muted me-auto">
                {pendingSizes.length} selected
              </small>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowSizesModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setFormData((prev) => ({ ...prev, sizes: pendingSizes }));
                  setShowSizesModal(false);
                }}
              >
                <i className="mdi mdi-check me-1"></i> Done
              </Button>
            </Modal.Footer>
          </Modal>

          {/*   buttons
           ********************************************* */}
          <div className="pt-2 border-top">
            <div className="d-flex justify-content-end">
              <button
                onClick={() => {
                  setShowAddModal(false);
                }}
                className="btn btn-white bg-white btn-sm px-3"
                type="button"
              >
                <i className="mdi mdi-close mr-1"></i> Cancel
              </button>
              <button
                disabled={loading}
                className="btn btn-primary btn-sm ms-1 px-3 ml-2"
                type="submit"
              >
                {isUpdateMode ? (
                  <>
                    <i className="mdi mdi-content-save mr-1"></i> Update
                  </>
                ) : (
                  <>
                    <i className="mdi mdi-plus-circle mr-1"></i> Add
                  </>
                )}
              </button>
              {isUpdateMode && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    UpdateProductsData(e, true);
                  }}
                  type="button"
                  className="ms-1 btn btn-secondary"
                >
                  Update & Close
                </button>
              )}
            </div>
          </div>
        </form>
      )}
      {activeTab === "variations" && (
        <div>
          {!isUpdateMode ? (
            <div className="text-center text-muted py-5">
              <i
                className="mdi mdi-information-outline d-block mb-1"
                style={{ fontSize: "20px" }}
              ></i>
              Please save the product first to manage variations.
            </div>
          ) : (
            <div>
              {/* Summary */}
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex gap-3">
                  <span
                    className="bg-info px-2 py-1 text-white rounded"
                    style={{
                      cursor: "auto",
                    }}
                  >
                    <i className="mdi mdi-table mr-1"></i>
                    {displayVariations.length} Total Variations
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm px-3"
                    onClick={handleDeleteAllVariations}
                    disabled={
                      variationsLoading || displayVariations.length === 0
                    }
                  >
                    <i className="mdi mdi-delete-sweep mr-1"></i> Delete All
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm px-3"
                    onClick={createOrUpdateVariations}
                    disabled={
                      variationsLoading ||
                      !formData.styleId ||
                      (formData.colors || []).length === 0
                    }
                  >
                    {variationsLoading ? (
                      <>
                        <i className="mdi mdi-loading mdi-spin mr-1"></i>{" "}
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="mdi mdi-refresh mr-1"></i> Create Or
                        Update Variations
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Hints */}
              {(!formData.styleId || (formData.colors || []).length === 0) && (
                <div
                  className="alert alert-warning py-2 mb-3"
                  style={{ fontSize: "13px" }}
                >
                  <i className="mdi mdi-alert-outline mr-1"></i>
                  {!formData.styleId && <span>Style ID is required. </span>}
                  {(formData.colors || []).length === 0 && (
                    <span>Add at least one color. </span>
                  )}
                  Go to the <strong>Product</strong> tab to fill in missing
                  data.
                </div>
              )}

              {/* Variations Table */}
              {(() => {
                return displayVariations.length > 0 ? (
                  <div
                    className="table-responsive"
                    style={{ maxHeight: "400px", overflowY: "auto" }}
                  >
                    <table className="table table-sm table-bordered table-hover align-middle mb-0">
                      <thead
                        className="table-light"
                        style={{ position: "sticky", top: 0, zIndex: 1 }}
                      >
                        <tr>
                          <th style={{ width: "40px" }}>#</th>
                          <th>Variation Name</th>
                          <th style={{ width: "120px" }}>Color</th>
                          <th style={{ width: "80px" }} className="text-center">
                            Image
                          </th>
                          <th
                            style={{ width: "140px" }}
                            className="text-center"
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayVariations.map((v, idx) => (
                          <tr key={v.id || idx}>
                            <td className="text-muted">{idx + 1}</td>
                            <td>
                              <span style={{ fontSize: "13px" }}>
                                {v.variationName}
                              </span>
                            </td>
                            <td>
                              <span className="">{v.color}</span>
                            </td>
                            <td className="text-center">
                              {(v.images || []).length > 0 ? (
                                <span className="badge bg-success">Yes</span>
                              ) : (
                                <span className="badge bg-secondary">No</span>
                              )}
                            </td>
                            <td className="text-center">
                              <div className="d-flex gap-1 justify-content-center">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary py-0 px-2"
                                  onClick={() => {
                                    setSelectedVariation(v);
                                    setShowImageModal(true);
                                  }}
                                  title="Manage Images"
                                >
                                  <i className="mdi mdi-image-multiple"></i>
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger py-0 px-2"
                                  onClick={() => handleDeleteVariation(v)}
                                  disabled={variationsLoading}
                                  title="Delete Variation"
                                >
                                  <i className="mdi mdi-trash-can-outline"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div
                    className="text-center text-muted py-4"
                    style={{ fontSize: "13px" }}
                  >
                    <i
                      className="mdi mdi-table-off d-block mb-1"
                      style={{ fontSize: "24px" }}
                    ></i>
                    No variations yet. Click &ldquo;Create Or Update
                    Variations&rdquo; to generate.
                  </div>
                );
              })()}

              {/* Variation Image Management Modal */}
              {showImageModal && selectedVariation && (
                <div
                  className="modal show d-block"
                  style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
                  tabIndex="-1"
                >
                  <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                    <div className="modal-content">
                      <div className="modal-header py-2">
                        <div>
                          <h5 className="modal-title mb-0">
                            <i className="mdi mdi-image-multiple mr-1"></i>
                            Images &mdash;{" "}
                            <span className="text-info">
                              {selectedVariation.variationName}
                            </span>
                          </h5>
                          <small className="text-muted">
                            <span className="badge bg-info text-dark me-1">
                              {selectedVariation.color}
                            </span>
                          </small>
                        </div>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => {
                            setShowImageModal(false);
                            setSelectedVariation(null);
                            setVariationImageFile(null);
                          }}
                        ></button>
                      </div>

                      <div className="modal-body">
                        {/* Upload Section */}
                        <div className="card border-0 bg-light mb-3">
                          <div className="card-body py-2 px-3">
                            <label className="form-label mb-1 fw-semibold">
                              <i className="mdi mdi-upload mr-1"></i> Add New
                              Image
                            </label>
                            {(selectedVariation.images || []).length > 0 ? (
                              <div
                                className="alert alert-warning py-1 mb-0"
                                style={{ fontSize: "13px" }}
                              >
                                <i className="mdi mdi-alert-outline mr-1"></i>
                                Delete the existing image before uploading a new
                                one.
                              </div>
                            ) : (
                              <div className="d-flex align-items-center gap-2">
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="form-control form-control-sm"
                                  onChange={(e) =>
                                    setVariationImageFile(
                                      e.target.files[0] || null,
                                    )
                                  }
                                />
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm px-3"
                                  onClick={handleUploadVariationImage}
                                  disabled={
                                    !variationImageFile || imageUploading
                                  }
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {imageUploading ? (
                                    <>
                                      <i className="mdi mdi-loading mdi-spin mr-1"></i>
                                      Uploading...
                                    </>
                                  ) : (
                                    <>
                                      <i className="mdi mdi-upload mr-1"></i>
                                      Upload
                                    </>
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Images Grid */}
                        {(selectedVariation.images || []).length === 0 ? (
                          <div className="text-center text-muted py-4">
                            <i
                              className="mdi mdi-image-off-outline d-block mb-1"
                              style={{ fontSize: "32px" }}
                            ></i>
                            No images uploaded for this variation.
                          </div>
                        ) : (
                          <div className="row g-2">
                            {(selectedVariation.images || []).map(
                              (imgKey, imgIdx) => (
                                <div
                                  key={imgIdx}
                                  className="col-6 col-md-4 col-lg-3"
                                >
                                  <div className="card border shadow-sm h-100">
                                    <ImgWithSignedUrl
                                      path={imgKey}
                                      className="card-img-top"
                                      styles={{
                                        objectFit: "cover",
                                        height: "120px",
                                        width: "100%",
                                      }}
                                    />
                                    <div className="card-footer p-1 text-center">
                                      <small
                                        className="d-block text-muted mb-1"
                                        style={{
                                          fontSize: "10px",
                                          wordBreak: "break-all",
                                        }}
                                      >
                                        {imgKey.split("/").pop()}
                                      </small>
                                      <button
                                        type="button"
                                        className="btn btn-danger btn-sm py-0 px-2"
                                        onClick={() =>
                                          handleDeleteVariationImage(imgKey)
                                        }
                                        title="Delete Image"
                                      >
                                        <i className="mdi mdi-trash-can-outline"></i>{" "}
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        )}
                      </div>

                      <div className="modal-footer py-2">
                        <small className="text-muted me-auto">
                          {(selectedVariation.images || []).length > 0
                            ? "1 image attached"
                            : "No image attached"}
                        </small>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm px-3"
                          onClick={() => {
                            setShowImageModal(false);
                            setSelectedVariation(null);
                            setVariationImageFile(null);
                          }}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {activeTab === "information" && (
        <SProductInformation
          formData={formData}
          setFormData={setFormData}
          isUpdateMode={isUpdateMode}
          onSaveProduct={(e) => UpdateProductsData(e, false)}
        />
      )}

      {activeTab === "files" && (
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-secondary bg-opacity-25 border-0 py-1">
                <h5 className="text-secondary mb-0">
                  <i className="mdi mdi-paperclip mr-1"></i> Attachments
                </h5>
              </div>
              <div className="card-body p-2">
                {isUpdateMode ? (
                  <SProductFiles selected={formData} isUpdateMode={isUpdateMode} />
                ) : (
                  <div className="text-center text-muted py-5">
                    <i
                      className="mdi mdi-information-outline d-block mb-1"
                      style={{ fontSize: "20px" }}
                    ></i>
                    Please save the product first to manage files.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
